import { ImageHero2, ImageHero2Props } from "@tvg/fuse-design/image-hero-2";
import useGlobalConfig from "client/context/GlobalConfig";
import useRender from "client/hooks/useRender";
import { isPersonalisedConfig } from "client/utils/amplience";
import getEventHandler from "client/utils/eventHandler";
import buildGoogleAnalyticsTag from "client/utils/getDataAnalyticsId";
import getImageSources from "client/utils/getImageSources";
import { toLowerCase } from "common/utils/string";
import React, { useCallback, useMemo, useRef } from "react";
import { AmpHeroBannerConfig, AmpImageHeroConfig, AmpPersonalisation } from "types/amplience";
import { backgroundConfig } from "./config";
import useCTAEvents from "../../hooks/useCTAEvents";

export interface ImageHeroProps extends Partial<Omit<ImageHero2Props, "variant">> {
  config: AmpHeroBannerConfig;
  position: number;
  lazyLoad?: boolean;
  highPriority?: boolean;
}

const useRepresentative = () => {
  const rendererRef = useRef(globalThis.__renderer);
  rendererRef.current = globalThis.__renderer;
  return useCallback((text?: string) => text && rendererRef.current && text, []);
};

const ImageHero: React.FC<ImageHeroProps> = ({
  config,
  highPriority,
  lazyLoad,
  position,
  ...props
}: ImageHeroProps) => {
  const { brand } = useGlobalConfig();
  const render = useRender();
  const represent = useRepresentative();

  const dataAnalyticsId = useMemo(() => {
    const { headline, subheadline } = config;
    const cta = config?.cta as Array<{ text: string; url: string }>;

    return buildGoogleAnalyticsTag(
      "HeroBanner",
      headline || subheadline ? `${headline || subheadline} - ${cta && cta[0].text}` : `${cta && cta[0].text}`,
      position,
    );
  }, [config, position]);

  const configProps = useMemo((): Omit<ImageHero2Props, "variant"> & { personalisation?: AmpPersonalisation } => {
    const isPersonalised = isPersonalisedConfig(config);
    const c = !isPersonalised ? config : (config.imageHero3 as AmpImageHeroConfig);

    const primaryCTA = c.cta?.[0] ?? {};
    const secondaryCTA = c.cta?.[1] ?? {};

    return {
      personalisation: isPersonalised ? config.personalization : {},
      background: c.backgroundColour && backgroundConfig[c.backgroundColour],
      image: {
        alt: "",
        position: toLowerCase(c.imagePosition) ?? "left",
        fetchPriority: highPriority ? "high" : undefined,
        isLazy: lazyLoad,
        ...getImageSources(c.image, { brand, w: 740, qlt: "default" }),
      },
      link: {
        text: primaryCTA.text,
        href: primaryCTA.url,
        onClick: getEventHandler(primaryCTA.publishEvent),
      },
      secondaryLink: {
        text: secondaryCTA.text,
        href: secondaryCTA.url,
        onClick: getEventHandler(secondaryCTA.publishEvent),
      },
      text: {
        headline: c.headline,
        headlineSizeV3: toLowerCase(c.headlineSize),
        headlineTag: "h2",
        subheadline: c.subheadline,
        subheadlineTag: c.headline ? "h3" : "h2",
        description: render(c.descriptionRTE?.description),
        representative: represent(c.representative),
        terms: render(c.termsRTE?.terms),
      },
    };
  }, [brand, config, highPriority, lazyLoad, render, represent]);

  const ctaEvents = useCTAEvents(isPersonalisedConfig(config) ? config.imageHero3.cta : config.cta);

  return (
    <ImageHero2
      dataTestId="content-image-hero"
      variant="v3"
      {...props}
      {...configProps}
      dataAnalyticsId={dataAnalyticsId}
      data-cta-event={ctaEvents}
    />
  );
};

export default React.memo(ImageHero);
